import type {
  ComputedFieldDefinition,
  ComputedFieldDefinitionId,
} from "@repo/app-types/entities/computedField";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import type { PlaybookSectionNode } from "@repo/app-types/entities/playbook";
import {
  getDefaultDirectionForGroupSortField,
  type GradingsByGroupSort,
  type GradingsByGroupSortField,
  type TeamPerformanceByGroupRow,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { renderPlural } from "@repo/ui/helpers/helpers";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import { type FC, useCallback, useMemo } from "react";
import type { Key, SortDescriptor } from "react-aria-components";
import {
  buildPerformanceColumns,
  PerformanceColumnId,
  performanceColumnIdToSortField,
  performanceSortFieldToColumnId,
  sortPerformanceColumns,
  togglablePerformanceMetricColumnIdSet,
} from "./performanceMetricColumns.js";

export interface GroupsTableProps {
  rows: readonly TeamPerformanceByGroupRow[];
  medianPassRate: number | undefined;
  medianTalkRatio: number | undefined;
  medianOutcomeGreenRate: number | undefined;
  medianOutcomeGreenCount: number | undefined;
  averageRevenueBenchmark: number | undefined;
  medianRevenuePerHour: number | undefined;
  medianSecondsPerGreenOutcome: number | undefined;
  medianCustomerExperienceScore: number | undefined;
  outcomeDefinition: OutcomeDefinition | undefined;
  shouldShowKnowledgeBaseAdherenceColumn: boolean;
  shouldShowCustomerExperienceScoreColumn: boolean;
  /** Whether the top-level Playbook score column should be visible */
  showsPlaybookColumn?: boolean;
  /** Sections to display as separate columns in the table */
  selectedSections?: readonly PlaybookSectionNode[];
  /** Median section scores keyed by section ID */
  medianSectionScores?: Record<string, number>;
  /** Computed field definitions to render as dynamic toggleable columns. */
  computedFields?: readonly ComputedFieldDefinition[];
  /** Median values per computed field id, for gap-bar baselines. */
  medianComputedFieldValues?: Record<ComputedFieldDefinitionId, number | null>;
  sort: GradingsByGroupSort | undefined;
  onSortChange: (sort: GradingsByGroupSort | undefined) => void;
  isLoading?: boolean;
  isError?: boolean;
  onGroupAction: (row: TeamPerformanceByGroupRow) => void;
  onLoadMore?: () => void;
  isFullBleed?: boolean;
  /**
   * Set of toggleable performance metric column IDs the user has chosen
   * to display. When omitted, every toggleable column is visible.
   */
  selectedMetricColumnIds?: ReadonlySet<string>;
}

const GroupColumnId = {
  Name: "name",
  Calls: PerformanceColumnId.Calls,
} as const;

export const GroupsTable: FC<GroupsTableProps> = (props) => {
  const {
    rows,
    medianPassRate,
    medianTalkRatio,
    medianOutcomeGreenRate,
    medianOutcomeGreenCount,
    averageRevenueBenchmark,
    medianRevenuePerHour,
    medianSecondsPerGreenOutcome,
    medianCustomerExperienceScore,
    outcomeDefinition,
    shouldShowKnowledgeBaseAdherenceColumn,
    shouldShowCustomerExperienceScoreColumn,
    showsPlaybookColumn = true,
    selectedSections = [],
    medianSectionScores,
    computedFields,
    medianComputedFieldValues,
    isLoading,
    isError,
    onLoadMore,
    isFullBleed,
  } = props;

  const selectedMetricColumnIds =
    props.selectedMetricColumnIds ?? togglablePerformanceMetricColumnIdSet;

  const columns = useMemo(
    () =>
      buildColumns({
        benchmarks: {
          medianPassRate,
          medianTalkRatio,
          medianOutcomeGreenRate,
          medianOutcomeGreenCount,
          averageRevenueBenchmark,
          medianRevenuePerHour,
          medianSecondsPerGreenOutcome,
          medianCustomerExperienceScore,
          medianSectionScores,
          medianComputedFieldValues,
        },
        outcomeDefinition,
        shouldShowKnowledgeBaseAdherenceColumn,
        shouldShowCustomerExperienceScoreColumn,
        showsPlaybookColumn,
        selectedSections,
        computedFields: computedFields ?? [],
        selectedMetricColumnIds,
      }),
    [
      medianPassRate,
      medianTalkRatio,
      medianOutcomeGreenRate,
      medianOutcomeGreenCount,
      averageRevenueBenchmark,
      medianRevenuePerHour,
      medianSecondsPerGreenOutcome,
      medianCustomerExperienceScore,
      medianSectionScores,
      medianComputedFieldValues,
      outcomeDefinition,
      shouldShowKnowledgeBaseAdherenceColumn,
      shouldShowCustomerExperienceScoreColumn,
      showsPlaybookColumn,
      selectedSections,
      computedFields,
      selectedMetricColumnIds,
    ],
  );

  const sortField = props.sort?.field;
  const sortDirection = props.sort?.direction;
  const sortDescriptor = useMemo((): SortDescriptor | undefined => {
    const column = mapSortFieldToColumnId(sortField);
    if (!column) {
      return undefined;
    }

    return {
      column,
      direction: sortDirection === "asc" ? "ascending" : "descending",
    };
  }, [sortField, sortDirection]);

  const onSortChange = props.onSortChange;
  const handleSortChange = useCallback(
    (descriptor: SortDescriptor | undefined) => {
      const field = mapColumnIdToSortField(descriptor?.column);
      if (!field) {
        onSortChange(undefined);
        return;
      }

      const requestedDirection =
        descriptor?.direction === "ascending" ? "asc" : "desc";
      const didFieldChange = field !== sortField;

      onSortChange({
        field,
        // If we picked a new field, default to the field's default direction.
        direction: didFieldChange
          ? getDefaultDirectionForGroupSortField(field)
          : requestedDirection,
      });
    },
    [sortField, onSortChange],
  );

  return (
    <EntityTable<TeamPerformanceByGroupRow>
      aria-label="Teammate groups"
      columns={columns}
      entities={rows}
      isLoading={isLoading}
      isError={isError}
      onLoadMore={onLoadMore}
      emptyMessage="No teammate groups have activity in the selected window."
      onEntityAction={props.onGroupAction}
      isFullBleed={isFullBleed}
      sortDescriptor={sortDescriptor}
      onSortChange={handleSortChange}
    />
  );
};

function buildColumns({
  benchmarks,
  outcomeDefinition,
  shouldShowKnowledgeBaseAdherenceColumn,
  shouldShowCustomerExperienceScoreColumn,
  showsPlaybookColumn,
  selectedSections,
  computedFields,
  selectedMetricColumnIds,
}: {
  benchmarks: {
    medianPassRate: number | undefined;
    medianTalkRatio: number | undefined;
    medianOutcomeGreenRate: number | undefined;
    medianOutcomeGreenCount: number | undefined;
    averageRevenueBenchmark: number | undefined;
    medianRevenuePerHour: number | undefined;
    medianSecondsPerGreenOutcome: number | undefined;
    medianCustomerExperienceScore: number | undefined;
    medianSectionScores: Record<string, number> | undefined;
    medianComputedFieldValues:
      | Record<ComputedFieldDefinitionId, number | null>
      | undefined;
  };
  outcomeDefinition: OutcomeDefinition | undefined;
  shouldShowKnowledgeBaseAdherenceColumn: boolean;
  shouldShowCustomerExperienceScoreColumn: boolean;
  showsPlaybookColumn: boolean;
  selectedSections: readonly PlaybookSectionNode[];
  computedFields: readonly ComputedFieldDefinition[];
  selectedMetricColumnIds: ReadonlySet<string>;
}): readonly EntityTableColumnDefinition<TeamPerformanceByGroupRow>[] {
  const allowsSorting = true;

  const columns: EntityTableColumnDefinition<TeamPerformanceByGroupRow>[] = [
    {
      id: GroupColumnId.Name,
      pinnedToLeft: true,
      isRowHeader: true,
      allowsSorting,
      header: "Teammate group",
      renderCell: (row) => {
        return (
          <div className="flex items-center gap-2">
            <span className="w-[8rem] max-w-fit flex-auto truncate font-medium">
              {row.name || "Unknown group"}
            </span>

            <span className="w-[8rem] max-w-fit flex-auto truncate text-xs text-gray-600">
              {renderPlural(row.teammate_count, {
                one: "1 teammate",
                other: (n) => `${n.toLocaleString()} teammates`,
              })}
            </span>
          </div>
        );
      },
    },
  ];

  columns.push(
    ...buildPerformanceColumns<TeamPerformanceByGroupRow>({
      benchmarks,
      outcomeDefinition,
      shouldShowKnowledgeBaseAdherenceColumn,
      shouldShowCustomerExperienceScoreColumn,
      showsPlaybookColumn,
      selectedSections,
      computedFields,
      selectedMetricColumnIds,
    }),
  );

  if (selectedMetricColumnIds.has(GroupColumnId.Calls)) {
    columns.push({
      id: GroupColumnId.Calls,
      className: "w-0",
      allowsSorting,
      header: "Calls",
      renderCell: (row) => {
        const count = row.recording_count;

        if (count === undefined) {
          return <span className="text-muted-foreground">--</span>;
        }

        return <span>{count.toLocaleString()}</span>;
      },
    });
  }

  return sortPerformanceColumns(columns);
}

function mapColumnIdToSortField(
  columnId: Key | undefined,
): GradingsByGroupSortField | undefined {
  switch (columnId) {
    case GroupColumnId.Name:
      return "group_name";

    case GroupColumnId.Calls:
      return "recording_count";
  }

  return performanceColumnIdToSortField(columnId);
}

function mapSortFieldToColumnId(
  sortField: GradingsByGroupSortField | undefined,
): string | undefined {
  switch (sortField) {
    case "group_name":
      return GroupColumnId.Name;

    case "recording_count":
      return GroupColumnId.Calls;
  }

  return performanceSortFieldToColumnId(sortField);
}
